/**
 * Pharmedico Provider Locator (Phase 3.1)
 * List/search/filter/sort + Maps JavaScript API + ZIP Geocoding (25-mile radius).
 * Map config values are supplied at runtime by section Liquid — never hard-coded here.
 */
(function () {
  'use strict';

  var INITIAL_VISIBLE = 12;
  var PAGE_SIZE = 12;
  var SINGLE_RESULT_ZOOM = 13;
  var EMPTY_RESULT_ZOOM = 9;
  var ZIP_RADIUS_MILES = 25;
  var ZIP_DEBOUNCE_MS = 450;
  var ZIP_PATTERN = /^\d{5}$/;

  var PIN_RED = '#EA4335';
  var PIN_RED_DK = '#C5221F';
  var PIN_HALO = '#2e7d32';
  var PIN_BORDER = '#ffffff';
  var ORIGIN_FILL = '#1a1a2e';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function parseJsonScript(root, sel) {
    var node = qs(root, sel);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (err) {
      console.warn('[ph-locator] Failed to parse JSON node');
      return null;
    }
  }

  function parseProviders(root) {
    var data = parseJsonScript(root, '[data-ph-locator-providers]');
    return Array.isArray(data) ? data : [];
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function toNumber(value) {
    var n = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cssEscape(value) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * 3958.8 * Math.asin(Math.sqrt(a));
  }

  function formatDistance(miles) {
    if (miles == null || !Number.isFinite(miles)) return '';
    if (miles < 10) {
      var decimal = miles.toFixed(1);
      if (decimal === '1.0') return 'Approx. 1 mile away';
      if (decimal.slice(-2) === '.0') {
        return 'Approx. ' + decimal.slice(0, -2) + ' miles away';
      }
      return 'Approx. ' + decimal + ' miles away';
    }
    var whole = Math.round(miles);
    if (whole === 1) return 'Approx. 1 mile away';
    return 'Approx. ' + whole + ' miles away';
  }

  function phoneHref(phone) {
    var raw = String(phone || '').trim();
    if (!raw) return '';
    var hasPlus = raw.charAt(0) === '+';
    var digits = raw.replace(/\D+/g, '');
    if (!digits) return '';
    return 'tel:' + (hasPlus ? '+' + digits : digits);
  }

  var SERVICE_CLASS_MAP = {
    'hormone therapy': 'ph-provider-service--hormone-therapy',
    'weight management': 'ph-provider-service--weight-management',
    'thyroid care': 'ph-provider-service--thyroid-care',
    'dermatology & skin care': 'ph-provider-service--dermatology-skin-care',
    'pain management & ldn': 'ph-provider-service--pain-management-ldn',
    'pediatric care': 'ph-provider-service--pediatric-care',
    'veterinary care': 'ph-provider-service--veterinary-care'
  };

  function serviceBadgeClass(service) {
    var mapped = SERVICE_CLASS_MAP[normalizeText(service)];
    return mapped || 'ph-provider-service--unknown';
  }

  function directionsUrl(provider) {
    var lat = toNumber(provider.latitude);
    var lng = toNumber(provider.longitude);
    if (lat != null && lng != null) {
      return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(lat + ',' + lng);
    }
    var parts = [provider.address1, provider.address2, provider.city, provider.state, provider.zip]
      .filter(Boolean)
      .join(', ');
    if (!parts) return '';
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(parts);
  }

  function createEl(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null && text !== '') el.textContent = text;
    return el;
  }

  function isFiveDigitZip(value) {
    return ZIP_PATTERN.test(String(value || '').trim());
  }

  function componentByType(components, type) {
    if (!Array.isArray(components)) return '';
    for (var i = 0; i < components.length; i++) {
      var c = components[i];
      if (c && Array.isArray(c.types) && c.types.indexOf(type) !== -1) {
        return c.short_name || c.long_name || '';
      }
    }
    return '';
  }

  function mountProviderMedia(media, imageUrl, placeholderUrl) {
    if (imageUrl) {
      var img = createEl('img', 'ph-locator__card-img');
      img.src = String(imageUrl);
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 72;
      img.height = 72;
      if (placeholderUrl) {
        img.addEventListener('error', function onProviderImageError() {
          img.removeEventListener('error', onProviderImageError);
          img.classList.add('ph-locator__card-img--placeholder');
          img.removeAttribute('srcset');
          img.sizes = '';
          img.src = placeholderUrl;
        });
      }
      media.appendChild(img);
      return;
    }

    if (placeholderUrl) {
      var placeholder = createEl('img', 'ph-locator__card-img ph-locator__card-img--placeholder');
      placeholder.src = placeholderUrl;
      placeholder.alt = '';
      placeholder.loading = 'lazy';
      placeholder.decoding = 'async';
      placeholder.width = 72;
      placeholder.height = 72;
      media.appendChild(placeholder);
      return;
    }

    media.appendChild(createEl('span', 'ph-locator__card-img-fallback'));
  }

  function buildCard(provider, selectedId, onSelect, onFocusMap, placeholderUrl) {
    var article = createEl('article', 'ph-locator__card');
    article.id = String(provider.id || '');
    article.tabIndex = 0;
    article.setAttribute('data-provider-id', String(provider.id || ''));
    if (selectedId && selectedId === provider.id) article.classList.add('is-selected');

    article.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('a, button')) return;
      onSelect(provider.id);
    });
    article.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        if (event.target !== article) return;
        event.preventDefault();
        onSelect(provider.id);
      }
    });

    var media = createEl('div', 'ph-locator__card-media');
    media.setAttribute('aria-hidden', 'true');
    mountProviderMedia(media, provider.imageUrl, placeholderUrl);
    article.appendChild(media);

    var body = createEl('div', 'ph-locator__card-body');
    body.appendChild(createEl('h3', 'ph-locator__card-title', provider.clinicName || 'Clinic'));

    if (provider.providerName) {
      var providerLine = createEl('p', 'ph-locator__card-provider', provider.providerName);
      if (provider.credentials) {
        providerLine.appendChild(createEl('span', 'ph-locator__card-credentials', ', ' + provider.credentials));
      }
      body.appendChild(providerLine);
    }

    var address = createEl('p', 'ph-locator__card-address');
    var line1 = [provider.address1, provider.address2].filter(Boolean).join(', ');
    var line2 = [provider.city, provider.state].filter(Boolean).join(', ');
    if (provider.zip) line2 = (line2 ? line2 + ' ' : '') + provider.zip;
    if (line1) address.appendChild(createEl('span', 'ph-locator__card-address-line', line1));
    if (line2) address.appendChild(createEl('span', 'ph-locator__card-city-line', line2));
    body.appendChild(address);

    var tel = phoneHref(provider.phone);
    if (tel && provider.phone) {
      var phoneRow = createEl('p', 'ph-locator__card-phone');
      phoneRow.appendChild(createEl('span', 'ph-locator__card-phone-label', 'Phone: '));
      var phoneLink = createEl('a', 'ph-locator__card-phone-link', String(provider.phone));
      phoneLink.href = tel;
      phoneRow.appendChild(phoneLink);
      body.appendChild(phoneRow);
    }

    if (provider.distanceMiles != null && Number.isFinite(provider.distanceMiles)) {
      body.appendChild(createEl('p', 'ph-locator__card-distance', formatDistance(provider.distanceMiles)));
    }

    if (Array.isArray(provider.services) && provider.services.length) {
      var services = createEl('ul', 'ph-locator__card-services');
      services.setAttribute('aria-label', 'Services');
      provider.services.forEach(function (service) {
        if (!service) return;
        services.appendChild(
          createEl('li', 'ph-locator__badge ' + serviceBadgeClass(service), String(service))
        );
      });
      body.appendChild(services);
    }

    if (provider.telehealth === true || provider.acceptingPatients === true) {
      var flags = createEl('ul', 'ph-locator__card-flags');
      flags.setAttribute('aria-label', 'Availability');
      if (provider.telehealth === true) {
        flags.appendChild(createEl('li', 'ph-locator__badge ph-locator__badge--soft', 'Telehealth'));
      }
      if (provider.acceptingPatients === true) {
        flags.appendChild(createEl('li', 'ph-locator__badge ph-locator__badge--soft', 'Accepting patients'));
      }
      body.appendChild(flags);
    }

    var actions = createEl('div', 'ph-locator__card-actions');
    if (provider.website) {
      var web = createEl('a', 'ph-locator__action', 'Website');
      web.href = String(provider.website);
      web.target = '_blank';
      web.rel = 'noopener noreferrer';
      actions.appendChild(web);
    }
    var dirs = directionsUrl(provider);
    if (dirs) {
      var dir = createEl('a', 'ph-locator__action', 'Directions');
      dir.href = dirs;
      dir.target = '_blank';
      dir.rel = 'noopener noreferrer';
      actions.appendChild(dir);
    }
    if (typeof onFocusMap === 'function') {
      var mapBtn = createEl('button', 'ph-locator__action ph-locator__action--map', 'View on map');
      mapBtn.type = 'button';
      mapBtn.setAttribute('data-ph-locator-focus-map', String(provider.id || ''));
      mapBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        onFocusMap(provider.id);
      });
      actions.appendChild(mapBtn);
    }
    body.appendChild(actions);
    article.appendChild(body);
    return article;
  }

  /* ---------- Google Maps bridge ---------- */

  function defaultUnavailableMessage(root) {
    return (
      root.getAttribute('data-map-unavailable') ||
      'Map unavailable. You can still browse providers and get directions from the list.'
    );
  }

  function renderUnavailable(mount, message, announceEl) {
    if (!mount) return;
    mount.setAttribute('data-map-ready', 'false');
    mount.setAttribute('aria-label', 'Provider map unavailable');
    mount.textContent = '';
    var box = createEl('div', 'ph-locator__map-placeholder ph-locator__map-placeholder--error');
    box.appendChild(createEl('p', 'ph-locator__map-title', 'Map unavailable'));
    box.appendChild(createEl('p', 'ph-locator__map-copy', message));
    mount.appendChild(box);
    if (announceEl) announceEl.textContent = message;
  }

  function clearMountForMap(mount) {
    mount.textContent = '';
    var canvas = createEl('div', 'ph-locator__map-canvas');
    canvas.setAttribute('data-ph-locator-map-canvas', '');
    mount.appendChild(canvas);
    return canvas;
  }

  function createMapsController() {
    var byRoot = new WeakMap();
    var zipCache = {};

    function getState(root) {
      var existing = byRoot.get(root);
      if (existing) return existing;
      var state = {
        map: null,
        libs: null,
        markers: {},
        originMarker: null,
        infoWindow: null,
        lastPayload: null,
        initPromise: null,
        failed: false,
        geocoder: null
      };
      byRoot.set(root, state);
      return state;
    }

    function mapsEnabled(root) {
      return root.getAttribute('data-maps-enabled') === 'true';
    }

    function mapIdFor(root) {
      return (root.getAttribute('data-google-map-id') || '').trim();
    }

    function waitForImportLibrary(timeoutMs) {
      return new Promise(function (resolve, reject) {
        var start = Date.now();
        (function poll() {
          if (window.google && google.maps && typeof google.maps.importLibrary === 'function') {
            resolve();
            return;
          }
          if (Date.now() - start > timeoutMs) {
            reject(new Error('maps_script_timeout'));
            return;
          }
          window.setTimeout(poll, 50);
        })();
      });
    }

    function loadLibraries(root) {
      var state = getState(root);
      if (state.libs) return Promise.resolve(state.libs);
      if (state.initPromise) return state.initPromise;

      state.initPromise = waitForImportLibrary(12000)
        .then(function () {
          return Promise.all([
            google.maps.importLibrary('maps'),
            google.maps.importLibrary('marker'),
            google.maps.importLibrary('geocoding'),
            google.maps.importLibrary('core')
          ]);
        })
        .then(function (parts) {
          var mapsLib = parts[0];
          var markerLib = parts[1];
          var geoLib = parts[2];
          if (!mapsLib || !mapsLib.Map || !markerLib || !markerLib.AdvancedMarkerElement) {
            throw new Error('advanced_marker_unavailable');
          }
          state.libs = {
            Map: mapsLib.Map,
            AdvancedMarkerElement: markerLib.AdvancedMarkerElement,
            PinElement: markerLib.PinElement || null,
            LatLngBounds: google.maps.LatLngBounds,
            InfoWindow: mapsLib.InfoWindow || google.maps.InfoWindow,
            Geocoder: (geoLib && geoLib.Geocoder) || google.maps.Geocoder,
            event: google.maps.event
          };
          state.geocoder = new state.libs.Geocoder();
          return state.libs;
        })
        .catch(function (err) {
          state.failed = true;
          state.initPromise = null;
          throw err;
        });

      return state.initPromise;
    }

    function ensureMap(root, mount, defaultCenter) {
      var state = getState(root);
      if (state.map) return Promise.resolve(state.map);
      return loadLibraries(root).then(function (libs) {
        var mapId = mapIdFor(root);
        if (!mapId) throw new Error('map_id_missing');
        var canvas = qs(mount, '[data-ph-locator-map-canvas]') || clearMountForMap(mount);
        state.map = new libs.Map(canvas, {
          center: defaultCenter,
          zoom: EMPTY_RESULT_ZOOM,
          mapId: mapId,
          gestureHandling: 'cooperative',
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false
        });
        state.infoWindow = new libs.InfoWindow();
        mount.setAttribute('data-map-ready', 'true');
        mount.setAttribute('aria-label', 'Interactive provider map');
        return state.map;
      });
    }

    function buildPin(libs, selected) {
      if (!libs.PinElement) return null;
      return new libs.PinElement({
        background: selected ? PIN_RED_DK : PIN_RED,
        borderColor: selected ? PIN_HALO : PIN_BORDER,
        glyphColor: '#ffffff',
        scale: selected ? 1.25 : 1.05
      });
    }

    function buildOriginContent() {
      var el = createEl('div', 'ph-locator__origin-marker');
      el.setAttribute('aria-hidden', 'true');
      el.title = 'Search ZIP origin';
      return el;
    }

    function buildInfoContent(provider) {
      var wrap = createEl('div', 'ph-locator__info');
      wrap.appendChild(createEl('p', 'ph-locator__info-title', provider.clinicName || 'Clinic'));
      var place = [provider.city, provider.state].filter(Boolean).join(', ');
      if (provider.zip) place = (place ? place + ' ' : '') + provider.zip;
      if (place) wrap.appendChild(createEl('p', 'ph-locator__info-line', place));
      return wrap;
    }

    function syncMarkerSelection(state, selectedId) {
      Object.keys(state.markers).forEach(function (id) {
        var entry = state.markers[id];
        if (!entry || !entry.marker || !state.libs) return;
        var selected = id === selectedId;
        var pin = buildPin(state.libs, selected);
        if (pin) entry.marker.content = pin.element;
        entry.marker.zIndex = selected ? 1000 : 1;
      });
    }

    function updateOriginMarker(state, zipOrigin) {
      if (state.originMarker) {
        state.originMarker.map = null;
        state.originMarker = null;
      }
      if (!zipOrigin || !state.map || !state.libs) return;
      state.originMarker = new state.libs.AdvancedMarkerElement({
        map: state.map,
        position: { lat: zipOrigin.lat, lng: zipOrigin.lng },
        title: 'Search origin: ' + (zipOrigin.label || 'ZIP'),
        content: buildOriginContent(),
        gmpClickable: false,
        zIndex: 0
      });
    }

    function fitToProviders(state, providers, defaultCenter, zipOrigin) {
      if (!state.map || !state.libs) return;
      var withCoords = providers.filter(function (p) {
        return p.latitude != null && p.longitude != null;
      });

      if (!withCoords.length) {
        if (zipOrigin) {
          state.map.setCenter({ lat: zipOrigin.lat, lng: zipOrigin.lng });
          state.map.setZoom(11);
        } else {
          state.map.setCenter(defaultCenter);
          state.map.setZoom(EMPTY_RESULT_ZOOM);
        }
        return;
      }

      if (withCoords.length === 1 && !zipOrigin) {
        state.map.setCenter({ lat: withCoords[0].latitude, lng: withCoords[0].longitude });
        state.map.setZoom(SINGLE_RESULT_ZOOM);
        return;
      }

      var bounds = new state.libs.LatLngBounds();
      withCoords.forEach(function (p) {
        bounds.extend({ lat: p.latitude, lng: p.longitude });
      });
      if (zipOrigin) bounds.extend({ lat: zipOrigin.lat, lng: zipOrigin.lng });
      state.map.fitBounds(bounds, 64);
    }

    function upsertMarkers(root, state, payload) {
      var libs = state.libs;
      var providers = payload.providers || [];
      var selectedId = payload.selectedId;
      var keep = {};

      providers.forEach(function (provider) {
        if (provider.latitude == null || provider.longitude == null) return;
        var id = String(provider.id || '');
        keep[id] = true;
        var existing = state.markers[id];
        var pin = buildPin(libs, selectedId === provider.id);
        if (existing && existing.marker) {
          existing.marker.position = { lat: provider.latitude, lng: provider.longitude };
          existing.provider = provider;
          if (pin) existing.marker.content = pin.element;
          existing.marker.title = provider.clinicName || 'Clinic';
          existing.marker.zIndex = selectedId === provider.id ? 1000 : 1;
          return;
        }

        var markerOptions = {
          map: state.map,
          position: { lat: provider.latitude, lng: provider.longitude },
          title: provider.clinicName || 'Clinic',
          gmpClickable: true,
          zIndex: selectedId === provider.id ? 1000 : 1
        };
        if (pin) markerOptions.content = pin.element;

        var marker = new libs.AdvancedMarkerElement(markerOptions);
        marker.addEventListener('gmp-click', function () {
          if (typeof payload.onMarkerSelect === 'function') {
            payload.onMarkerSelect(provider.id);
          }
        });
        state.markers[id] = { marker: marker, provider: provider };
      });

      Object.keys(state.markers).forEach(function (id) {
        if (keep[id]) return;
        state.markers[id].marker.map = null;
        delete state.markers[id];
      });

      syncMarkerSelection(state, selectedId);
      updateOriginMarker(state, payload.zipOrigin || null);
    }

    function openInfoFor(state, providerId) {
      var entry = state.markers[providerId];
      if (!entry || !state.infoWindow || !state.map) return;
      state.infoWindow.setContent(buildInfoContent(entry.provider));
      state.infoWindow.open({ map: state.map, anchor: entry.marker });
    }

    function update(payload) {
      if (!payload || !payload.root || !payload.mount) return;
      var root = payload.root;
      var mount = payload.mount;
      var announce = qs(root, '[data-ph-locator-map-status]');
      var message = defaultUnavailableMessage(root);
      var defaultCenter = payload.defaultCenter || { lat: 27.3364, lng: -82.5307 };
      var state = getState(root);
      state.lastPayload = payload;

      if (!mapsEnabled(root) || !mapIdFor(root)) {
        renderUnavailable(mount, message, announce);
        return;
      }
      if (state.failed) {
        renderUnavailable(mount, message, announce);
        return;
      }
      if (!qs(mount, '[data-ph-locator-map-canvas]')) {
        clearMountForMap(mount);
      }

      ensureMap(root, mount, defaultCenter)
        .then(function () {
          upsertMarkers(root, state, payload);
          fitToProviders(state, payload.providers || [], defaultCenter, payload.zipOrigin || null);
          if (payload.selectedId && state.markers[payload.selectedId] && payload.focusSelected) {
            openInfoFor(state, payload.selectedId);
            var marker = state.markers[payload.selectedId].marker;
            if (marker && marker.position) state.map.panTo(marker.position);
          } else if (state.infoWindow) {
            state.infoWindow.close();
          }
        })
        .catch(function () {
          state.failed = true;
          renderUnavailable(mount, message, announce);
        });
    }

    function resize(root) {
      var state = byRoot.get(root);
      if (!state || !state.map || !state.libs || !state.lastPayload) return;
      if (state.libs.event) state.libs.event.trigger(state.map, 'resize');
      fitToProviders(
        state,
        state.lastPayload.providers || [],
        state.lastPayload.defaultCenter || { lat: 27.3364, lng: -82.5307 },
        state.lastPayload.zipOrigin || null
      );
    }

    function focusProvider(root, providerId) {
      var state = byRoot.get(root);
      if (!state || !state.map || !state.markers[providerId]) return;
      var entry = state.markers[providerId];
      syncMarkerSelection(state, providerId);
      state.map.panTo(entry.marker.position);
      if (state.map.getZoom() < SINGLE_RESULT_ZOOM - 1) state.map.setZoom(SINGLE_RESULT_ZOOM);
      if (state.lastPayload) {
        openInfoFor(state, providerId);
      }
    }

    function scrollMapIntoView(root) {
      var panel = qs(root, '[data-ph-locator-map-panel]');
      if (!panel) return;
      panel.scrollIntoView({
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function geocodeZip(root, zip) {
      var cached = zipCache[zip];
      if (cached) return Promise.resolve(cached);

      return loadLibraries(root).then(function () {
        var state = getState(root);
        if (!state.geocoder) throw new Error('geocoder_missing');
        return new Promise(function (resolve, reject) {
          state.geocoder.geocode(
            {
              address: zip,
              componentRestrictions: { country: 'US', postalCode: zip }
            },
            function (results, status) {
              if (status !== 'OK' || !results || !results.length) {
                reject(new Error(String(status || 'ZERO_RESULTS')));
                return;
              }
              var best = null;
              for (var i = 0; i < results.length; i++) {
                var r = results[i];
                if (r.types && r.types.indexOf('postal_code') !== -1) {
                  best = r;
                  break;
                }
              }
              best = best || results[0];
              var loc = best.geometry && best.geometry.location;
              if (!loc) {
                reject(new Error('NO_LOCATION'));
                return;
              }
              var comps = best.address_components || [];
              var city =
                componentByType(comps, 'locality') ||
                componentByType(comps, 'sublocality') ||
                componentByType(comps, 'postal_town') ||
                componentByType(comps, 'administrative_area_level_3');
              var stateAbbr = componentByType(comps, 'administrative_area_level_1');
              var resolvedZip = componentByType(comps, 'postal_code') || zip;
              var payload = {
                zip: zip,
                lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
                lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
                city: city,
                state: stateAbbr,
                label: [city, stateAbbr].filter(Boolean).join(', ') + (resolvedZip ? ' ' + resolvedZip : '')
              };
              zipCache[zip] = payload;
              resolve(payload);
            }
          );
        });
      });
    }

    return {
      update: update,
      resize: resize,
      focusProvider: focusProvider,
      scrollMapIntoView: scrollMapIntoView,
      geocodeZip: geocodeZip,
      ensureReady: function (root, mount, defaultCenter) {
        if (!mapsEnabled(root)) return Promise.reject(new Error('maps_disabled'));
        return ensureMap(root, mount, defaultCenter);
      }
    };
  }

  window.PharmedicoProviderLocatorMaps = createMapsController();

  /* ---------- Locator UI ---------- */

  function initLocator(root) {
    if (!root || root.getAttribute('data-ph-locator-ready') === 'true') return;
    root.setAttribute('data-ph-locator-ready', 'true');

    var radiusMiles = toNumber(root.getAttribute('data-zip-radius-miles')) || ZIP_RADIUS_MILES;
    var placeholderUrl = (root.getAttribute('data-provider-placeholder') || '').trim();
    var providers = parseProviders(root).map(function (item) {
      return {
        id: item.id,
        sourceId: item.sourceId || '',
        clinicName: item.clinicName || '',
        providerName: item.providerName || '',
        credentials: item.credentials || '',
        address1: item.address1 || '',
        address2: item.address2 || '',
        city: item.city || '',
        state: item.state || '',
        zip: String(item.zip || ''),
        latitude: toNumber(item.latitude),
        longitude: toNumber(item.longitude),
        phone: item.phone || '',
        website: item.website || '',
        services: Array.isArray(item.services) ? item.services.filter(Boolean) : [],
        telehealth: item.telehealth === true ? true : null,
        acceptingPatients: item.acceptingPatients === true ? true : null,
        imageUrl: item.imageUrl || null,
        description: item.description || null,
        distanceMiles: null
      };
    });

    var results = qs(root, '[data-ph-locator-results]');
    var countEl = qs(root, '[data-ph-locator-count]');
    var distanceNoteEl = qs(root, '[data-ph-locator-distance-note]');
    var searchInput = qs(root, '[data-ph-locator-search]');
    var clearBtn = qs(root, '[data-ph-locator-clear]');
    var resolvedEl = qs(root, '[data-ph-locator-resolved]');
    var serviceSelect = qs(root, '[data-ph-locator-service]');
    var sortSelect = qs(root, '[data-ph-locator-sort]');
    var geoBtn = qs(root, '[data-ph-locator-geo]');
    var layout = qs(root, '[data-ph-locator-layout]');
    var mapMount = qs(root, '[data-ph-locator-map]');
    var mapPanel = qs(root, '[data-ph-locator-map-panel]');
    var emptyDirectoryMsg = root.getAttribute('data-empty-directory') || 'No providers are available to display yet.';
    var emptyFilterMsg = root.getAttribute('data-empty-filter') || 'No providers match your search.';
    var locationDeniedMsg =
      root.getAttribute('data-location-denied') ||
      'Location access was denied. You can still search by city, ZIP, or clinic name.';
    var zipFailMsg =
      root.getAttribute('data-zip-fail') ||
      'We couldn’t locate that ZIP code. Try another ZIP, city, or clinic name.';
    var zipEmptyTpl =
      root.getAttribute('data-zip-empty') ||
      'No providers found within 25 miles of [place]. Try another ZIP or use the map to explore.';
    var mapsEnabled = root.getAttribute('data-maps-enabled') === 'true';

    var state = {
      query: '',
      service: '',
      sort: 'name',
      origin: null,
      zipMode: false,
      zipOrigin: null,
      zipRequestId: 0,
      zipPending: null,
      zipTimer: null,
      selectedId: null,
      visibleCount: INITIAL_VISIBLE,
      filtered: [],
      focusSelected: false,
      userChoseNearest: false
    };

    function setCountMessage(text) {
      if (countEl) countEl.textContent = text;
    }

    function updateDistanceNote(show) {
      if (!distanceNoteEl) return;
      if (show) distanceNoteEl.removeAttribute('hidden');
      else distanceNoteEl.setAttribute('hidden', '');
    }

    function updateClearVisibility() {
      if (!clearBtn || !searchInput) return;
      var hasText = !!(searchInput.value && String(searchInput.value).length);
      if (hasText) clearBtn.removeAttribute('hidden');
      else clearBtn.setAttribute('hidden', '');
    }

    function setResolvedLabel(text) {
      if (!resolvedEl) return;
      if (text) {
        resolvedEl.textContent = text;
        resolvedEl.hidden = false;
      } else {
        resolvedEl.textContent = '';
        resolvedEl.hidden = true;
      }
    }

    function updateSortOptions() {
      if (!sortSelect) return;
      var nearest = sortSelect.querySelector('option[value="nearest"]');
      if (!nearest) return;
      if (state.origin || state.zipMode) {
        nearest.disabled = false;
        nearest.textContent = 'Nearest';
      } else {
        nearest.disabled = true;
        nearest.textContent = 'Nearest (set your location)';
        if (sortSelect.value === 'nearest' && !state.zipMode) {
          sortSelect.value = 'name';
          state.sort = 'name';
        }
      }
    }

    function matchesQuery(provider, query) {
      if (!query) return true;
      var haystack = [
        provider.clinicName,
        provider.providerName,
        provider.address1,
        provider.address2,
        provider.city,
        provider.state,
        provider.zip
      ]
        .map(normalizeText)
        .join(' ');
      return haystack.indexOf(query) !== -1;
    }

    function matchesService(provider, service) {
      if (!service) return true;
      return (provider.services || []).some(function (item) {
        return normalizeText(item) === normalizeText(service);
      });
    }

    function scrollCardIntoView(id) {
      if (!results) return;
      var card = results.querySelector('[data-provider-id="' + cssEscape(String(id)) + '"]');
      if (!card) return;
      card.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function isNarrow() {
      return window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
    }

    function selectProvider(id, options) {
      options = options || {};
      state.selectedId = id;
      state.focusSelected = !!options.focusMap;
      render();
      if (options.scrollCard) scrollCardIntoView(id);
      if (options.focusMap && window.PharmedicoProviderLocatorMaps) {
        if (isNarrow()) window.PharmedicoProviderLocatorMaps.scrollMapIntoView(root);
        window.PharmedicoProviderLocatorMaps.focusProvider(root, id);
      }
      state.focusSelected = false;
    }

    function exitZipMode(options) {
      options = options || {};
      state.zipMode = false;
      state.zipOrigin = null;
      state.zipPending = null;
      if (state.zipTimer) {
        window.clearTimeout(state.zipTimer);
        state.zipTimer = null;
      }
      state.zipRequestId += 1;
      setResolvedLabel('');
      if (!options.keepSort) {
        if (state.origin && state.userChoseNearest) {
          state.sort = 'nearest';
          if (sortSelect) sortSelect.value = 'nearest';
        } else {
          state.sort = 'name';
          if (sortSelect) sortSelect.value = 'name';
          state.userChoseNearest = false;
        }
      }
      updateSortOptions();
    }

    function applyFilters() {
      var filtered;

      if (state.zipMode && state.zipOrigin) {
        filtered = providers.filter(function (provider) {
          if (!matchesService(provider, state.service)) return false;
          if (provider.latitude == null || provider.longitude == null) return false;
          provider.distanceMiles = haversineMiles(
            state.zipOrigin.lat,
            state.zipOrigin.lng,
            provider.latitude,
            provider.longitude
          );
          return provider.distanceMiles <= radiusMiles;
        });
        filtered.sort(function (a, b) {
          var da = a.distanceMiles == null ? Number.POSITIVE_INFINITY : a.distanceMiles;
          var db = b.distanceMiles == null ? Number.POSITIVE_INFINITY : b.distanceMiles;
          if (da !== db) return da - db;
          return normalizeText(a.clinicName).localeCompare(normalizeText(b.clinicName));
        });
      } else {
        var query = normalizeText(state.query);
        filtered = providers.filter(function (provider) {
          return matchesQuery(provider, query) && matchesService(provider, state.service);
        });
        if (state.origin) {
          filtered.forEach(function (provider) {
            if (provider.latitude != null && provider.longitude != null) {
              provider.distanceMiles = haversineMiles(
                state.origin.lat,
                state.origin.lng,
                provider.latitude,
                provider.longitude
              );
            } else {
              provider.distanceMiles = null;
            }
          });
        } else {
          filtered.forEach(function (provider) {
            provider.distanceMiles = null;
          });
        }
        filtered.sort(function (a, b) {
          if (state.sort === 'nearest' && state.origin) {
            var da = a.distanceMiles == null ? Number.POSITIVE_INFINITY : a.distanceMiles;
            var db = b.distanceMiles == null ? Number.POSITIVE_INFINITY : b.distanceMiles;
            if (da !== db) return da - db;
          }
          return normalizeText(a.clinicName).localeCompare(normalizeText(b.clinicName));
        });
      }

      if (state.selectedId && !filtered.some(function (p) { return p.id === state.selectedId; })) {
        state.selectedId = null;
      }

      state.filtered = filtered;
      if (state.visibleCount < INITIAL_VISIBLE) state.visibleCount = INITIAL_VISIBLE;
      render();
    }

    function notifyMap() {
      if (!mapMount || !window.PharmedicoProviderLocatorMaps) return;
      window.PharmedicoProviderLocatorMaps.update({
        root: root,
        mount: mapMount,
        providers: state.filtered,
        selectedId: state.selectedId,
        focusSelected: state.focusSelected,
        origin: state.origin,
        zipOrigin: state.zipOrigin,
        defaultCenter: {
          lat: toNumber(root.getAttribute('data-default-lat')) || 27.3364,
          lng: toNumber(root.getAttribute('data-default-lng')) || -82.5307
        },
        onMarkerSelect: function (id) {
          selectProvider(id, { scrollCard: true, focusMap: false });
        },
        onCardFocus: function (id) {
          selectProvider(id, { scrollCard: true, focusMap: false });
        }
      });
    }

    function render() {
      if (!results) return;
      results.textContent = '';

      if (!providers.length) {
        var emptyDir = createEl('div', 'ph-locator__empty');
        emptyDir.setAttribute('data-ph-locator-empty', 'directory');
        emptyDir.appendChild(createEl('p', null, emptyDirectoryMsg));
        results.appendChild(emptyDir);
        setCountMessage('No providers to show yet.');
        updateDistanceNote(false);
        notifyMap();
        return;
      }

      if (!state.filtered.length) {
        var emptyFilter = createEl('div', 'ph-locator__empty');
        emptyFilter.setAttribute('data-ph-locator-empty', state.zipMode ? 'zip-radius' : 'filter');
        var emptyText = emptyFilterMsg;
        if (state.zipMode && state.zipOrigin) {
          emptyText = zipEmptyTpl.replace('[place]', state.zipOrigin.label || state.zipOrigin.zip);
        }
        emptyFilter.appendChild(createEl('p', null, emptyText));
        results.appendChild(emptyFilter);
        if (state.zipMode && state.zipOrigin) {
          setCountMessage('0 providers within ' + radiusMiles + ' miles of ' + state.zipOrigin.label);
          updateDistanceNote(true);
        } else {
          setCountMessage('0 providers match');
          updateDistanceNote(false);
        }
        notifyMap();
        return;
      }

      var visible = state.filtered.slice(0, state.visibleCount);
      visible.forEach(function (provider) {
        results.appendChild(
          buildCard(
            provider,
            state.selectedId,
            function (id) {
              selectProvider(id, { focusMap: false });
            },
            mapsEnabled
              ? function (id) {
                  selectProvider(id, { focusMap: true, scrollCard: false });
                }
              : null,
            placeholderUrl
          )
        );
      });

      if (state.filtered.length > state.visibleCount) {
        var moreWrap = createEl('div', 'ph-locator__status');
        var moreBtn = createEl('button', 'ph-locator__btn ph-locator__btn--secondary', 'Load more');
        moreBtn.type = 'button';
        moreBtn.addEventListener('click', function () {
          state.visibleCount += PAGE_SIZE;
          render();
        });
        moreWrap.appendChild(moreBtn);
        results.appendChild(moreWrap);
      }

      var total = state.filtered.length;
      var label;
      var showDistanceNote = false;
      if (state.zipMode && state.zipOrigin) {
        label =
          (total === 1 ? '1 provider' : total + ' providers') +
          ' within ' +
          radiusMiles +
          ' miles of ' +
          state.zipOrigin.label;
        showDistanceNote = true;
      } else {
        label = total === 1 ? '1 provider' : total + ' providers';
        if (state.origin && state.sort === 'nearest') label += ' sorted by nearest';
        showDistanceNote = state.filtered.some(function (provider) {
          return provider.distanceMiles != null && Number.isFinite(provider.distanceMiles);
        });
      }
      setCountMessage(label);
      updateDistanceNote(showDistanceNote);
      notifyMap();
    }

    function runZipSearch(zip) {
      if (!mapsEnabled || !window.PharmedicoProviderLocatorMaps) {
        setCountMessage(zipFailMsg);
        setResolvedLabel('');
        state.zipMode = false;
        state.zipOrigin = null;
        applyFilters();
        return;
      }

      var requestId = ++state.zipRequestId;
      state.zipPending = zip;

      window.PharmedicoProviderLocatorMaps
        .ensureReady(root, mapMount, {
          lat: toNumber(root.getAttribute('data-default-lat')) || 27.3364,
          lng: toNumber(root.getAttribute('data-default-lng')) || -82.5307
        })
        .catch(function () {
          return null;
        })
        .then(function () {
          return window.PharmedicoProviderLocatorMaps.geocodeZip(root, zip);
        })
        .then(function (origin) {
          if (requestId !== state.zipRequestId) return;
          if (String(state.query).trim() !== zip) return;
          state.zipMode = true;
          state.zipOrigin = origin;
          state.zipPending = null;
          state.sort = 'nearest';
          if (sortSelect) sortSelect.value = 'nearest';
          updateSortOptions();
          setResolvedLabel(origin.label);
          applyFilters();
        })
        .catch(function () {
          if (requestId !== state.zipRequestId) return;
          if (String(state.query).trim() !== zip) return;
          state.zipMode = false;
          state.zipOrigin = null;
          state.zipPending = null;
          setResolvedLabel('');
          setCountMessage(zipFailMsg);
          // Fall back to local text match on the ZIP digits so browsing still works.
          applyFilters();
        });
    }

    function scheduleZipOrText(rawValue, immediate) {
      var value = String(rawValue || '');
      state.query = value;
      updateClearVisibility();

      if (state.zipTimer) {
        window.clearTimeout(state.zipTimer);
        state.zipTimer = null;
      }

      if (isFiveDigitZip(value)) {
        var zip = value.trim();
        if (immediate) {
          runZipSearch(zip);
        } else {
          state.zipTimer = window.setTimeout(function () {
            state.zipTimer = null;
            if (String(state.query).trim() !== zip) return;
            runZipSearch(zip);
          }, ZIP_DEBOUNCE_MS);
        }
        return;
      }

      if (state.zipMode || state.zipOrigin) {
        exitZipMode({ keepSort: false });
      }
      state.visibleCount = INITIAL_VISIBLE;
      applyFilters();
    }

    function clearSearch() {
      if (state.zipTimer) {
        window.clearTimeout(state.zipTimer);
        state.zipTimer = null;
      }
      state.zipRequestId += 1;
      state.query = '';
      if (searchInput) searchInput.value = '';
      exitZipMode({ keepSort: false });
      state.visibleCount = INITIAL_VISIBLE;
      updateClearVisibility();
      applyFilters();
      if (searchInput) searchInput.focus();
    }

    if (searchInput) {
      searchInput.addEventListener('input', function (event) {
        scheduleZipOrText(event.target.value, false);
      });
      searchInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          scheduleZipOrText(searchInput.value, true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          clearSearch();
        }
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearSearch();
      });
    }

    if (serviceSelect) {
      serviceSelect.addEventListener('change', function (event) {
        state.service = event.target.value || '';
        state.visibleCount = INITIAL_VISIBLE;
        applyFilters();
      });
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function (event) {
        state.sort = event.target.value || 'name';
        state.userChoseNearest = state.sort === 'nearest';
        if (!state.zipMode) applyFilters();
      });
    }

    if (geoBtn) {
      geoBtn.addEventListener('click', function () {
        if (!navigator.geolocation) {
          setCountMessage(locationDeniedMsg);
          return;
        }
        geoBtn.disabled = true;
        geoBtn.textContent = 'Locating…';
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            state.origin = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude
            };
            if (!state.zipMode) {
              state.sort = 'nearest';
              state.userChoseNearest = true;
              if (sortSelect) sortSelect.value = 'nearest';
            }
            updateSortOptions();
            geoBtn.disabled = false;
            geoBtn.textContent = 'Use my location';
            applyFilters();
          },
          function () {
            geoBtn.disabled = false;
            geoBtn.textContent = 'Use my location';
            var note = createEl('div', 'ph-locator__status');
            note.appendChild(createEl('p', null, locationDeniedMsg));
            if (results) results.insertBefore(note, results.firstChild);
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
        );
      });
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (window.PharmedicoProviderLocatorMaps) {
          window.PharmedicoProviderLocatorMaps.resize(root);
        }
      }, 150);
    });

    updateClearVisibility();
    updateSortOptions();
    applyFilters();

    if (mapsEnabled && mapMount && window.PharmedicoProviderLocatorMaps) {
      window.PharmedicoProviderLocatorMaps
        .ensureReady(root, mapMount, {
          lat: toNumber(root.getAttribute('data-default-lat')) || 27.3364,
          lng: toNumber(root.getAttribute('data-default-lng')) || -82.5307
        })
        .then(function () {
          notifyMap();
          window.PharmedicoProviderLocatorMaps.resize(root);
        })
        .catch(function () {});
    }
  }

  function boot() {
    qsa(document, '[data-ph-provider-locator]').forEach(initLocator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    qsa(event.target, '[data-ph-provider-locator]').forEach(function (root) {
      root.removeAttribute('data-ph-locator-ready');
      initLocator(root);
    });
  });
})();
